import { useConfig } from "@/context/use-context";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import API from "./api";

type Granularity = "HOURLY" | "DAILY" | "MONTHLY";
type Energy = "ELEC" | "GAS";

export type FetchConsumptionDataResult = {
	data: ConsumptionData[];
};

type ConsumptionData = {
	timestamp: string;
	consumption: number;
	cost: number;
	consumption_hp: number;
	cost_hp: number;
	consumption_hc: number;
	cost_hc: number;
	subscription_cost: number;
	consumption_base: number;
	cost_base: number;
	temperature: number;
	top_100_consumption: number;
	top_100_cost: number;
	top_100_subscription_cost: number;
	worst_100_consumption: number;
	worst_100_cost: number;
	worst_100_subscription_cost: number;
	average_100_consumption: number;
	average_100_cost: number;
	average_100_subscription_cost: number;
};

type FetchConsumptionDataParams = {
	pod: string;
	bearerToken: string;
	energy: Energy;
	start_date?: string;
	end_date?: string;
	granularity: Granularity;
	include_baseline?: boolean;
};

async function fetchConsumptionData({
	energy,
	start_date,
	end_date,
	granularity,
	include_baseline = false,
	pod,
	bearerToken,
}: FetchConsumptionDataParams) {
	if (!pod || !bearerToken) {
		throw new Error("Pod or bearer token is required");
	}
	try {
		const response = await API.get<FetchConsumptionDataResult>(
			`/pods/${pod}/consumption_data`,
			{
				headers: {
					Authorization: `Bearer ${bearerToken}`,
				},
				params: {
					energy,
					start_date,
					end_date,
					granularity: granularity || "HOURLY",
					include_baseline,
				},
			},
		);
		return response.data;
	} catch (error) {
		if (error instanceof AxiosError) {
			if (error.response?.data.detail === "No consumption data found") {
				return {
					data: [],
				};
			}
		}
		throw error;
	}
}

// React Query Hook
export const useConsumptionData = ({
	disabled,
	include_baseline = false,
	...params
}: {
	energy: Energy;
	start_date?: string;
	end_date?: string;
	granularity: Granularity;
	include_baseline?: boolean;
	disabled?: boolean;
}) => {
	const config = useConfig();
	return useQuery({
		queryKey: ["consumptionData", { ...params, include_baseline }],
		queryFn: () =>
			fetchConsumptionData({
				...params,
				include_baseline,
				bearerToken: config.bearerToken,
				pod: config.pod?.pod_id ?? "",
			}),
		staleTime: 1000 * 60 * 60, // 1 heure
		enabled: !disabled,
	});
};
